import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import logo from '../assets/images/logo.png';
import { FaBars, FaTimes } from 'react-icons/fa';

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HeaderWrap = styled.header`
  position: fixed;
  top: 0; width: 100%;
  z-index: 1001;
  transition: all 0.35s ease;
  background: ${p => p.scrolled ? 'rgba(3,4,94,0.97)' : 'transparent'};
  backdrop-filter: ${p => p.scrolled ? 'blur(16px)' : 'none'};
  border-bottom: ${p => p.scrolled ? '1px solid rgba(0,180,216,0.15)' : 'none'};

  @media (max-width: 768px) {
    background: rgba(3,4,94,0.97);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(0,180,216,0.15);
  }
`;

const NavInner = styled.nav`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 4rem;
  height: 72px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  position: relative;

  @media (max-width: 768px) {
    height: 60px;
    padding: 0 1.5rem;
    display: flex;
    justify-content: center;
  }
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  justify-content: flex-end;

  @media (max-width: 768px) { display: none; }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  justify-content: flex-start;

  @media (max-width: 768px) { display: none; }
`;

const Logo = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 2.5rem;

  img {
    height: 48px;
    transition: opacity 0.3s ease;
    @media (max-width: 768px) { height: 38px; }
    &:hover { opacity: 0.8; }
  }
`;

const NavLink = styled(Link)`
  color: ${p => p.active ? '#00b4d8' : 'rgba(255,255,255,0.75)'};
  font-weight: 700;
  font-size: 0.82rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  padding: 4px 0;
  transition: color 0.25s ease;

  &.lowercase { text-transform: none; }

  &::after {
    content: '';
    position: absolute;
    bottom: -6px; left: 0;
    width: ${p => p.active ? '100%' : '0'};
    height: 1px;
    background: #00b4d8;
    transition: width 0.25s ease;
  }

  &:hover { color: #ffffff; &::after { width: 100%; } }
`;

const ContactBtn = styled(Link)`
  padding: 9px 22px;
  background: #00b4d8;
  color: #03045e;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  transition: all 0.25s ease;
  white-space: nowrap;

  &:hover { background: #90e0ef; }
`;

const MenuBtn = styled.button`
  display: none;
  background: none;
  border: none;
  color: rgba(255,255,255,0.8);
  font-size: 1.3rem;
  transition: color 0.25s;
  position: absolute;
  right: 1.5rem;
  &:hover { color: #00b4d8; }

  @media (max-width: 768px) { display: flex; }
`;

const MobileMenu = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${p => p.open ? 'flex' : 'none'};
    flex-direction: column;
    position: fixed;
    top: 60px; left: 0; right: 0;
    background: rgba(3,4,94,0.98);
    backdrop-filter: blur(16px);
    padding: 2rem;
    gap: 1.5rem;
    border-bottom: 1px solid rgba(0,180,216,0.15);
    animation: ${slideDown} 0.25s ease;
    z-index: 1000;
  }
`;

const Overlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${p => p.open ? 'block' : 'none'};
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 999;
  }
`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const active = path => location.pathname === path ? 1 : 0;

  return (
    <>
      <HeaderWrap scrolled={scrolled ? 1 : 0}>
        <NavInner>
          <NavLeft>
            <NavLink to="/" active={active("/")}>Beranda</NavLink>
            <NavLink to="/fuel-water-hybrid" active={active("/fuel-water-hybrid")}>FUEL WATER HYBRID</NavLink>
          </NavLeft>

          <Logo to="/"><img src={logo} alt="Hidrogen" /></Logo>

          <NavRight>
            <NavLink to="/hidrogenpasta" active={active("/hidrogenpasta")}>HIDROGENPASTA</NavLink>
            <NavLink to="/hidrofit" active={active("/hidrofit")} className="lowercase">hidrofit⁺</NavLink>
            <ContactBtn to="/kontak">Kontak</ContactBtn>
          </NavRight>

          <MenuBtn onClick={() => setOpen(!open)}>
            {open ? <FaTimes /> : <FaBars />}
          </MenuBtn>
        </NavInner>
      </HeaderWrap>

      <MobileMenu open={open}>
        <NavLink to="/" active={active("/")}>Beranda</NavLink>
        <NavLink to="/fuel-water-hybrid" active={active("/fuel-water-hybrid")}>FUEL WATER HYBRID</NavLink>
        <NavLink to="/hidrogenpasta" active={active("/hidrogenpasta")}>HIDROGENPASTA</NavLink>
        <NavLink to="/hidrofit" active={active("/hidrofit")} className="lowercase">hidrofit⁺</NavLink>
        <ContactBtn to="/kontak">Kontak</ContactBtn>
      </MobileMenu>

      <Overlay open={open} onClick={() => setOpen(false)} />
    </>
  );
}
